import React from 'react';
import { Route, Routes } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';

// Public Pages
import HomePage from '../pages/HomePage';
import RegisterPage from '../pages/RegisterPage';
import LoginPage from '../pages/LoginPage';
import VerifyOtpPage from '../pages/VerifyOtpPage';
import AllCategoriesPage from '../pages/AllCategoriesPage';
import CategoryPage from '../pages/CategoryPage';
import SearchResultsPage from '../pages/SearchResultsPage';
import JobBoardPage from '../pages/JobBoardPage';
import JobDetailsPage from '../pages/JobDetailsPage';
import WorkerPublicProfile from '../pages/WorkerPublicProfile';
import WorkerDetailsPage from '../pages/WorkerDetailsPage';
import AboutPage from '../pages/AboutPage';
import ContactPage from '../pages/ContactPage';
import FAQPage from '../pages/FAQPage';
import GuidePage from '../pages/GuidePage';
import DevelopersPage from '../pages/DevelopersPage';

// Protected User Pages
import CompleteProfilePage from '../pages/CompleteProfilePage';
import OwnerDashboard from '../pages/OwnerDashboard';
import WorkerDashboard from '../pages/WorkerDashboard';
import WorkerProfilePage from '../pages/WorkerProfilePage';
import MyJobsPage from '../pages/MyJobsPage';
import JobManagementPage from '../pages/JobManagementPage';
import MyHiresPage from '../pages/MyHiresPage';
import RunningJobs from '../pages/RunningJobs';
import WalletPage from '../pages/WalletPage';
import MessagesPage from '../pages/MessagesPage';
import NotificationsPage from '../pages/NotificationsPage';
import MobileMenuPage from '../pages/MobileMenuPage';
import PublishJob from '../pages/PublishJob';
import HireWorkerPage from '../pages/HireWorkerPage';
import WorkerJobMap from '../pages/WorkerJobMap';
import BuyerTrackingMap from '../pages/BuyerTrackingMap';
import ViewApplicantsPage from '../pages/ViewApplicantsPage';
import JobBiddersPage from '../pages/JobBiddersPage';
import ApplicantDetailsPage from '../pages/ApplicantDetailsPage';
import UserProfilePage from '../pages/UserProfilePage';
import OwnerProfilePage from '../pages/OwnerProfilePage';
import OwnerDetailsPage from '../pages/OwnerDetailsPage';
import OwnerBidsPage from '../pages/OwnerBidsPage';
import WorkerBidsPage from '../pages/WorkerBidsPage';
import WorkerRatingsPage from '../pages/WorkerRatingsPage';
import WorkerEarningsPage from '../pages/WorkerEarningsPage';
import WorkerInstrumentsPage from '../pages/WorkerInstrumentsPage';
import BecomeBrokerPage from '../pages/BecomeBrokerPage';
import BrokerTeam from '../pages/BrokerTeam';

// Admin Pages
import AdminLayout from '../components/AdminLayout';
import AdminDashboard from '../pages/AdminDashboard';
import AdminUsersPage from '../pages/AdminUsersPage';
import AdminJobsPage from '../pages/AdminJobsPage';
import AdminCategoriesPage from '../pages/AdminCategoriesPage';
import AdminFormBuilderPage from '../pages/AdminFormBuilderPage';
import AdminVerificationsPage from '../pages/AdminVerificationsPage';
import AdminWithdrawalsPage from '../pages/AdminWithdrawalsPage';
import AdminWalletPage from '../pages/AdminWalletPage';

// Layouts
import RoleBasedLayout from '../components/RoleBasedLayout';
import DashboardLayout from '../components/DashboardLayout';

// Broker Pages
import BrokerOverview from '../pages/BrokerOverview';
import BrokerRosterPage from '../pages/BrokerRosterPage';
import BrokerFindWorkersPage from '../pages/BrokerFindWorkersPage';
import BrokerActivitiesPage from '../pages/BrokerActivitiesPage';
import BrokerProfilePage from '../pages/BrokerProfilePage';

import { FaTachometerAlt, FaUsers, FaSearch, FaGavel, FaMoneyBill, FaUserCircle } from 'react-icons/fa';

const AppRouter = ({ workers, workersLoading, workersError }) => {

  const brokerNavItems = [
    { path: '/broker-dashboard', exact: true, icon: <FaTachometerAlt />, name: 'Overview' },
    { path: 'roster', icon: <FaUsers />, name: 'My Roster' },
    { path: 'find-workers', icon: <FaSearch />, name: 'Find Workers' },
    { path: 'activities', icon: <FaGavel />, name: 'My Activities' },
    { path: 'earnings', icon: <FaMoneyBill />, name: 'Earnings' },
    { path: 'profile', icon: <FaUserCircle />, name: 'My Profile' },
  ];

  return (
    <Routes>
      {/* --- Public Routes --- */}
      <Route path="/" element={<HomePage workers={workers} workersLoading={workersLoading} workersError={workersError} />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/verify-otp" element={<VerifyOtpPage />} />
      <Route path="/categories" element={<AllCategoriesPage />} />
      <Route path="/category/:categoryName" element={<CategoryPage />} />
      <Route path="/search" element={<SearchResultsPage />} />
      <Route path="/jobs" element={<JobBoardPage />} />
      <Route path="/job/:jobId" element={<JobDetailsPage />} />
      <Route path="/worker/:id" element={<WorkerDetailsPage />} />
      <Route path="/workers/profile/:profileId" element={<WorkerPublicProfile />} />
      
      {/* Informational */}
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/guide" element={<GuidePage />} />
      <Route path="/developers" element={<DevelopersPage />} />

      {/* --- Protected Shared Routes (NO SIDEBAR) --- */}
      <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfilePage /></ProtectedRoute>} />
      <Route path="/become-a-broker" element={<ProtectedRoute><BecomeBrokerPage /></ProtectedRoute>} />
      <Route path="/menu" element={<ProtectedRoute><MobileMenuPage /></ProtectedRoute>} />

      {/* --- Buyer / Worker Dashboard Routes (WITH SIDEBAR) --- */}
      <Route element={<RoleBasedLayout />}>
        {/* Shared Dashboard Items */}
        <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />

        {/* Buyer Specialized */}
        <Route path="/owner-dashboard" element={<ProtectedRoute><OwnerDashboard /></ProtectedRoute>} />
        <Route path="/my-jobs" element={<ProtectedRoute><MyJobsPage /></ProtectedRoute>} />
        <Route path="/my-hires" element={<ProtectedRoute><MyHiresPage /></ProtectedRoute>} />
        <Route path="/manage-job/:jobId" element={<ProtectedRoute><JobManagementPage /></ProtectedRoute>} />
        <Route path="/publish-job" element={<ProtectedRoute><PublishJob /></ProtectedRoute>} />
        <Route path="/publish-job/edit/:jobId" element={<ProtectedRoute><PublishJob /></ProtectedRoute>} />
        <Route path="/hire/:workerId" element={<ProtectedRoute><HireWorkerPage /></ProtectedRoute>} />
        <Route path="/track-worker/:hireId" element={<ProtectedRoute><BuyerTrackingMap /></ProtectedRoute>} />
        <Route path="/applicants/:jobId" element={<ProtectedRoute><ViewApplicantsPage /></ProtectedRoute>} />
        <Route path="/job-bidders/:jobId" element={<ProtectedRoute><JobBiddersPage /></ProtectedRoute>} />
        <Route path="/applicant-details/:applicantId" element={<ProtectedRoute><ApplicantDetailsPage /></ProtectedRoute>} />
        <Route path="/owner-profile" element={<ProtectedRoute><OwnerProfilePage /></ProtectedRoute>} />
        <Route path="/owner-details" element={<ProtectedRoute><OwnerDetailsPage /></ProtectedRoute>} />
        <Route path="/owner/bids" element={<ProtectedRoute><OwnerBidsPage /></ProtectedRoute>} />
        <Route path="/profile/:userId" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
      </Route>

      {/* --- Worker Dashboard Routes (WITH SIDEBAR) --- */}
      <Route path="/worker-dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<WorkerDashboard />} />
        <Route path="running-jobs" element={<RunningJobs />} />
        <Route path="broker-team" element={<BrokerTeam />} />
        <Route path="bids" element={<WorkerBidsPage />} />
        <Route path="ratings" element={<WorkerRatingsPage />} />
        <Route path="earnings" element={<WorkerEarningsPage />} />
        <Route path="instruments" element={<WorkerInstrumentsPage />} />
        <Route path="profile" element={<WorkerProfilePage />} />
      </Route>
      <Route path="/job-tracking/:hireId" element={<ProtectedRoute><WorkerJobMap /></ProtectedRoute>} />

      {/* --- Broker Dashboard Routes (WITH SIDEBAR) --- */}
      <Route path="/broker-dashboard" element={<ProtectedRoute><DashboardLayout navItems={brokerNavItems} theme="blue" /></ProtectedRoute>}>
        <Route index element={<BrokerOverview />} />
        <Route path="roster" element={<BrokerRosterPage />} />
        <Route path="find-workers" element={<BrokerFindWorkersPage />} />
        <Route path="activities" element={<BrokerActivitiesPage />} />
        <Route path="earnings" element={<WorkerEarningsPage />} />
        <Route path="profile" element={<BrokerProfilePage />} />
      </Route>

      {/* --- Admin Panel (WITH SIDEBAR) --- */}
      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="jobs" element={<AdminJobsPage />} />
          <Route path="categories" element={<AdminCategoriesPage />} />
          <Route path="forms" element={<AdminFormBuilderPage />} />
          <Route path="verifications" element={<AdminVerificationsPage />} />
          <Route path="withdrawals" element={<AdminWithdrawalsPage />} />
          <Route path="wallet" element={<AdminWalletPage />} />
      </Route>
    </Routes>
  );
};

export default AppRouter;
